import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useCatalogPresentationOptional } from 'context/CatalogPresentationContext';
import type { OfferAppearance } from 'types/product';
import { customerBadgeText, defaultOfferAppearance, isOfferOverlay, offerBadgeText } from 'utils/offerAppearance';
import { resolveStoreMediaUrl } from 'utils/themeDisplay';

export const useOfferLook = (override?: OfferAppearance): OfferAppearance => {
  const presentation = useCatalogPresentationOptional();
  return override ?? presentation?.offerAppearance ?? defaultOfferAppearance();
};

interface OfferBadgeProps {
  percent: number;
  look?: OfferAppearance;
  /** No mostrar nada si la insignia va como cocarda sobre la foto (tarjeta y ficha). */
  hideWhenOverlay?: boolean;
}

/** Insignia del porcentaje de descuento junto al precio o en el carrito. */
export const OfferBadge: React.FC<OfferBadgeProps> = ({ percent, look: override, hideWhenOverlay = false }) => {
  const look = useOfferLook(override);
  if (!look.badgeVisible || percent <= 0) return null;
  if (hideWhenOverlay && isOfferOverlay(look)) return null;
  const text = offerBadgeText(look, percent);

  if (look.badgeDisplay === 'text') {
    return (
      <Typography component="span" variant="caption" sx={{ color: look.badgeColor, fontWeight: 800 }}>
        {text}
      </Typography>
    );
  }

  if (isOfferOverlay(look)) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          component="img"
          src={resolveStoreMediaUrl(look.badgeImageUrl ?? '')}
          alt=""
          sx={{ width: 20, height: 20, objectFit: 'contain' }}
        />
        <Typography component="span" variant="caption" fontWeight={800} sx={{ color: look.badgeColor }}>
          {text}
        </Typography>
      </Box>
    );
  }

  const outlined = look.badgeVariant === 'outlined';
  return (
    <Chip
      size="small"
      label={text}
      variant={outlined ? 'outlined' : 'filled'}
      sx={{
        height: 20,
        fontWeight: 700,
        ...(outlined
          ? { borderColor: look.badgeColor, color: look.badgeTextColor, bgcolor: 'transparent' }
          : { bgcolor: look.badgeColor, color: look.badgeTextColor }),
      }}
    />
  );
};

const POSITION_SX = {
  topLeft: { top: 8, left: 8 },
  topRight: { top: 8, right: 8 },
  bottomLeft: { bottom: 8, left: 8 },
  bottomRight: { bottom: 8, right: 8 },
} as const;

interface OfferBadgeOverlayProps {
  percent?: number;
  look?: OfferAppearance;
  scale?: number;
}

/** Cocarda de descuento sobre la foto del producto. El contenedor debe ser position: relative. */
export const OfferBadgeOverlay: React.FC<OfferBadgeOverlayProps> = ({ percent, look: override, scale = 1 }) => {
  const look = useOfferLook(override);
  if (!percent || percent <= 0 || !isOfferOverlay(look)) return null;
  const size = Math.round(look.badgeImageSize * scale);
  const text = offerBadgeText(look, percent);
  return (
    <Box
      title={text}
      sx={{
        position: 'absolute',
        ...POSITION_SX[look.badgeImagePosition],
        width: size,
        height: size,
        zIndex: 2,
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component="img"
        src={resolveStoreMediaUrl(look.badgeImageUrl ?? '')}
        alt={text}
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
      />
      {look.badgeTextOnImage ? (
        <Typography
          component="span"
          sx={{
            position: 'relative',
            color: look.badgeTextColor,
            fontWeight: 800,
            fontSize: Math.max(10, Math.round(size * 0.24)),
            lineHeight: 1,
            textAlign: 'center',
            px: 0.5,
          }}
        >
          {text}
        </Typography>
      ) : null}
    </Box>
  );
};

/** Un chip por cada tramo Desc 1/2/3 del cliente. */
export const CustomerDiscountChips: React.FC<{ percents: number[]; look?: OfferAppearance }> = ({
  percents,
  look: override,
}) => {
  const look = useOfferLook(override);
  if (!look.customerBadgeVisible || percents.length === 0) return null;
  const outlined = look.customerBadgeVariant === 'outlined';
  return (
    <>
      {percents.map((percent, index) => (
        <Chip
          key={`${index}-${percent}`}
          size="small"
          label={customerBadgeText(look, percent)}
          variant={outlined ? 'outlined' : 'filled'}
          sx={{
            height: 20,
            fontWeight: 700,
            ...(outlined
              ? { borderColor: look.customerBadgeColor, color: look.customerBadgeTextColor, bgcolor: 'transparent' }
              : { bgcolor: look.customerBadgeColor, color: look.customerBadgeTextColor }),
          }}
        />
      ))}
    </>
  );
};

/** Insignia de promoción del bloque de insignias. */
export const PromoFlagChip: React.FC<{ look?: OfferAppearance }> = ({ look: override }) => {
  const look = useOfferLook(override);
  return (
    <Chip
      size="small"
      label={look.promoLabel}
      sx={{ bgcolor: look.promoColor, color: look.promoTextColor, fontWeight: 600 }}
    />
  );
};
